// Analytics Helper Functions
// Common analytics tracking functions for use throughout the application

import analyticsService from '../services/analytics/analyticsService';

/**
 * Track when a user opens an app
 */
export function trackAppOpen(appName: string, windowId?: string, spaceId?: string) {
  analyticsService.trackAppUsage(appName, 'open', {
    window_id: windowId,
    space_id: spaceId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track when a user closes an app
 */
export function trackAppClose(appName: string, windowId?: string, timeSpent?: number) {
  analyticsService.trackAppUsage(appName, 'close', {
    window_id: windowId,
    time_spent_seconds: timeSpent,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track when a user performs a search - DISABLED
 */
export function trackSearch(searchTerm: string, searchType: string, resultCount?: number) {
  // Search tracking disabled - no Mixpanel event sent
  console.debug('[SEARCH] Tracking disabled:', { searchTerm: '[REDACTED]', searchType, resultCount });
}

/**
 * Track when a user logs in
 */
export function trackLogin(method: string, userId?: string) {
  analyticsService.trackLogin(method, userId);
}

/**
 * Track when a user signs up
 */
export function trackSignup(method: string, userId?: string) {
  analyticsService.trackSignup(method, userId);
}

/**
 * Trigger Google Ads conversion tracking for successful user signup
 * Safely calls gtag conversion event if available
 */
export function triggerGoogleAdsConversion() {
  try {
    // Check if gtag is available (loaded from the global script in index.html)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      console.log('🎯 [Analytics] Triggering Google Ads signup conversion');
      
      // Fire the Google Ads conversion event
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-10789905168/NPayCK2Wn6QbEJDGg5ko'
      });
      
      console.log('✅ [Analytics] Google Ads conversion event fired successfully');
    } else {
      console.warn('⚠️ [Analytics] gtag not available - skipping Google Ads conversion');
    }
  } catch (error) {
    // Non-blocking error - don't let conversion tracking failures affect user experience
    console.error('❌ [Analytics] Error firing Google Ads conversion:', error);
  }
}

/**
 * Track when a user interacts with AI chat - DISABLED
 */
export function trackAIChatInteraction(action: string, messageLength?: number, responseTime?: number) {
  // AI chat tracking disabled - no Mixpanel event sent
  console.debug('[AI_CHAT] Tracking disabled:', { action, messageLength, responseTime });
}

/**
 * Track when a user creates or edits a document - DISABLED
 */
export function trackDocumentAction(action: string, documentType?: string, documentId?: string) {
  // Document action tracking disabled - no Mixpanel event sent
  console.debug('[DOCUMENT_ACTION] Tracking disabled:', { action, documentType, documentId });
}

/**
 * Track when a user uses the PDF viewer - DISABLED
 */
export function trackPDFViewerAction(action: string, pdfId?: string, pageNumber?: number) {
  // PDF viewer tracking disabled - no Mixpanel event sent
  console.debug('[PDF_VIEWER] Tracking disabled:', { action, pdfId, pageNumber });
}

/**
 * Track when a user performs file operations - DISABLED
 */
export function trackFileOperation(operation: string, fileType?: string, fileSize?: number) {
  // File operation tracking disabled - no Mixpanel event sent
  console.debug('[FILE_OPERATION] Tracking disabled:', { operation, fileType, fileSize });
}

/**
 * Track when a user encounters an error
 */
export function trackError(errorMessage: string, errorType: string, componentName?: string) {
  analyticsService.trackError(errorMessage, errorType, {
    component_name: componentName,
    user_agent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track page views
 */
export function trackPageView(pageTitle?: string, pagePath?: string) {
  analyticsService.trackPageView({
    page_title: pageTitle || document.title,
    page_path: pagePath || window.location.pathname,
    properties: {
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
    }
  });
}

/**
 * Track business events (subscriptions, payments, etc.) - DISABLED
 */
export function trackBusinessEvent(eventName: string, value?: number, currency?: string) {
  // Business event tracking disabled - no Mixpanel event sent
  console.debug('[BUSINESS_EVENT] Tracking disabled:', { eventName, value, currency });
}

/**
 * Set user properties when user info is available
 */
export function setUserProperties(userId: string, properties: Record<string, any>) {
  analyticsService.setUserProperties({
    user_id: userId,
    properties: {
      ...properties,
      last_seen: new Date().toISOString(),
    }
  });
} 